import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Inbox, GitBranch, Calendar, CheckSquare, ArrowRight,
  TrendingUp, Workflow,
} from "lucide-react";
import { Skeleton, useToast, EmptyState } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatMoney, formatRelative } from "@/lib/format";
import type { Activity, Task, Conversation } from "@/lib/types";

interface Stats {
  contacts: number;
  contactsTotal: number;
  openOpps: number;
  revenueWon: number;
  tasksDueToday: number;
  upcomingAppointments: number;
  unreadMessages: number;
}

export function DashboardPage() {
  const toast = useToast();
  const [stats, setStats] = useState<Stats | null>(null);
  const [activities, setActivities] = useState<Activity[] | null>(null);
  const [todayTasks, setTodayTasks] = useState<Task[] | null>(null);
  const [recentConversations, setRecentConversations] = useState<Conversation[] | null>(null);

  useEffect(() => {
    // The four stat tiles are all cheap COUNTs — load them together and render
    // the moment they're back (~1s, even on a freshly-woken server) instead of
    // waiting on the heavier activity/task/conversation lists below. Previously
    // a single Promise.all gated every tile on the SLOWEST query, so one slow
    // list could leave the whole dashboard blank for 10-20s on a cold open.
    Promise.all([
      api.get<{ data: { value: number } }>("/dashboards/widget-data/contacts.count", { range: "30d" }),
      api.get<{ data: { value: number } }>("/dashboards/widget-data/contacts.total"),
      api.get<{ data: { value: number } }>("/dashboards/widget-data/opportunities.open_count"),
      api.get<{ data: { value: number } }>("/dashboards/widget-data/opportunities.revenue_won", { range: "30d" }),
      api.get<{ data: { value: number } }>("/dashboards/widget-data/tasks.due_today"),
      api.get<{ data: { value: number } }>("/dashboards/widget-data/appointments.upcoming"),
      api.get<{ data: { value: number } }>("/dashboards/widget-data/conversations.unread"),
    ])
      .then(([contacts, total, opps, rev, tasks, appts, unread]) => {
        setStats({
          contacts: contacts.data.value,
          contactsTotal: total.data.value,
          openOpps: opps.data.value,
          revenueWon: rev.data.value,
          tasksDueToday: tasks.data.value,
          upcomingAppointments: appts.data.value,
          unreadMessages: unread.data.value,
        });
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"));

    // Lists/feeds load independently — each fills in when ready and never holds
    // up the stat tiles. On failure we fall back to an empty list, so the panel
    // shows its own friendly empty state rather than spinning forever.
    api.get<{ activities: Activity[] }>("/activities", { limit: 8 })
      .then((r) => setActivities(r.activities)).catch(() => setActivities([]));
    api.get<{ tasks: Task[] }>("/tasks", { due: "today", limit: 5 })
      .then((r) => setTodayTasks(r.tasks)).catch(() => setTodayTasks([]));
    api.get<{ conversations: Conversation[] }>("/conversations", { status: "open", limit: 5 })
      .then((r) => setRecentConversations(r.conversations)).catch(() => setRecentConversations([]));
  }, [toast]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Welcome back</h1>
        <p className="mt-1 text-sm text-zinc-500">Here&rsquo;s what&rsquo;s happening in your CRM today.</p>
      </div>

      {!stats ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({length:4}).map((_,i)=><Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label="Contacts"
            value={stats.contactsTotal.toLocaleString()}
            sub={`+${stats.contacts} this month`}
            Icon={Users}
            href="/contacts"
          />
          <StatCard
            label="Open deals"
            value={stats.openOpps.toLocaleString()}
            sub={`${formatMoney(stats.revenueWon)} won (30d)`}
            Icon={GitBranch}
            href="/pipelines"
          />
          <StatCard
            label="Inbox"
            value={stats.unreadMessages.toLocaleString()}
            sub="unread messages"
            Icon={Inbox}
            href="/inbox"
            highlight={stats.unreadMessages > 0}
          />
          <StatCard
            label="Tasks today"
            value={stats.tasksDueToday.toLocaleString()}
            sub={`${stats.upcomingAppointments} appointments`}
            Icon={CheckSquare}
            href="/tasks"
            highlight={stats.tasksDueToday > 0}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Activity feed */}
        <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 lg:col-span-2">
          <div className="border-b border-zinc-800/70 px-5 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-200">Recent activity</h3>
            <Link to="/contacts" className="text-xs text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="p-5">
            {activities === null ? (
              <div className="space-y-3">
                {Array.from({length:4}).map((_,i)=><Skeleton key={i} className="h-10" />)}
              </div>
            ) : activities.length === 0 ? (
              <EmptyState icon={TrendingUp} title="Nothing yet" description="Activity will show up as you and your customers interact." className="py-8" />
            ) : (
              <ul className="space-y-3">
                {activities.map((a) => (
                  <li key={a.id} className="flex items-start gap-3 text-sm">
                    <div className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-indigo-500/60" />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-200">{a.summary || a.type}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {a.contact_id && a.contact_name && (
                          <Link to={`/contacts/${a.contact_id}`} className="text-indigo-300 hover:underline">{a.contact_name}</Link>
                        )}
                        {a.contact_name && " · "}
                        {formatRelative(a.occurred_at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Today's tasks + recent conversations */}
        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
            <div className="border-b border-zinc-800/70 px-4 py-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Today&rsquo;s tasks</h3>
              <Link to="/tasks" className="text-xs text-zinc-500 hover:text-zinc-300">All</Link>
            </div>
            {todayTasks === null ? (
              <div className="p-4 space-y-2">
                {Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-8" />)}
              </div>
            ) : todayTasks.length === 0 ? (
              <p className="px-4 py-6 text-xs text-zinc-500 text-center">No tasks due today</p>
            ) : (
              <ul className="divide-y divide-zinc-800/40">
                {todayTasks.map((t) => (
                  <li key={t.id} className="px-4 py-2.5">
                    <p className="text-sm text-zinc-200 truncate">{t.title}</p>
                    {t.contact_name && <p className="text-xs text-zinc-500 mt-0.5 truncate">{t.contact_name}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
            <div className="border-b border-zinc-800/70 px-4 py-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Recent conversations</h3>
              <Link to="/inbox" className="text-xs text-zinc-500 hover:text-zinc-300">Inbox</Link>
            </div>
            {recentConversations === null ? (
              <div className="p-4 space-y-2">
                {Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-8" />)}
              </div>
            ) : recentConversations.length === 0 ? (
              <p className="px-4 py-6 text-xs text-zinc-500 text-center">No conversations yet</p>
            ) : (
              <ul className="divide-y divide-zinc-800/40">
                {recentConversations.map((c) => (
                  <li key={c.id} className="px-4 py-2.5">
                    <Link to="/inbox" className="block">
                      <p className={"text-sm truncate " + (c.unread_count > 0 ? "font-semibold text-zinc-100" : "text-zinc-300")}>
                        {c.contact_name || c.contact_email || c.contact_phone || "Unknown"}
                      </p>
                      <p className="text-xs text-zinc-500 truncate mt-0.5">{c.last_message_preview ?? "(no preview)"}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, Icon, href, highlight }: {
  label: string; value: string; sub?: string;
  Icon: React.ComponentType<{ className?: string }>;
  href: string;
  highlight?: boolean;
}) {
  return (
    <Link
      to={href}
      className={
        "group relative overflow-hidden rounded-xl border bg-zinc-900/30 p-4 transition-colors " +
        (highlight ? "border-indigo-500/30 hover:border-indigo-500/50" : "border-zinc-800/70 hover:border-zinc-700")
      }
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
        <Icon className="h-3.5 w-3.5 text-zinc-600 group-hover:text-zinc-400" />
      </div>
      <p className="mt-2 text-2xl font-semibold text-zinc-100 tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-zinc-500 truncate">{sub}</p>}
    </Link>
  );
}
